import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getSupabase, isSupabaseConfigured } from '../lib/supabase';
import { upsertUserProfileFromAuth } from '../api/profileApi';
import { finishAuthenticatedSession } from '../lib/legalConsentGate';
import {
  beginSignupFlow,
  hasLocalePreferences,
  saveSession,
} from '../lib/onboardingStorage';
import { mapAuthErrorToKey } from '../lib/mapAuthError';
import { useMingo } from '../context/MingoContext';

function LoginModal() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, showToast, refreshAuth, closeLoginRequired } = useMingo();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);

  function goSignup() {
    closeLoginRequired();
    beginSignupFlow();
    if (hasLocalePreferences()) {
      navigate('/onboarding/auth?tab=signup', { replace: true });
    } else {
      navigate('/onboarding', { replace: true });
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!email.trim() || !password) return;
    setBusy(true);
    try {
      if (!isSupabaseConfigured) {
        showToast(t('common.preparing'));
        setBusy(false);
        return;
      }
      const sb = getSupabase();
      if (!sb) {
        setBusy(false);
        return;
      }
      const { error } = await sb.auth.signInWithPassword({ email: email.trim(), password });
      if (error) {
        showToast(t(mapAuthErrorToKey(error.message)));
        setBusy(false);
        return;
      }
      try {
        await upsertUserProfileFromAuth();
      } catch {
        /* no-op */
      }
      saveSession(email.trim());
      const {
        data: { session },
      } = await sb.auth.getSession();
      setPassword('');
      setBusy(false);
      closeLoginRequired();
      await finishAuthenticatedSession(navigate, {
        refreshAuth,
        checkSignupPrefs: false,
        preloadedUser: session?.user ?? null,
        nextPath: `${location.pathname}${location.search || ''}`,
      });
    } catch {
      showToast(t('errors.generic'));
      setBusy(false);
    }
  }

  return (
    <div className="mingo-modal-root" role="presentation" style={{ zIndex: 10060 }}>
      <button type="button" className="mingo-modal-backdrop" aria-label={t('common.close')} onClick={closeLoginRequired} />
      <form
        className="mingo-modal-dialog mingo-login-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="mingo-login-modal-title"
        onClick={(e) => e.stopPropagation()}
        onSubmit={handleSubmit}
      >
        <h2 id="mingo-login-modal-title" className="mingo-modal-dialog__title">
          {t('guest.loginRequiredTitle')}
        </h2>
        <p className="mingo-modal-dialog__body" style={{ marginBottom: 14 }}>
          {t('guest.loginRequiredMessage')}
        </p>
        <div className="mingo-login-modal__fields">
          <label className="mingo-login-modal__field">
            <span>{t('auth.email')}</span>
            <input
              type="email"
              className="mingo-login-modal__input"
              autoComplete="email"
              inputMode="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </label>
          <label className="mingo-login-modal__field">
            <span>{t('auth.password')}</span>
            <input
              type="password"
              className="mingo-login-modal__input"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </label>
        </div>
        <div className="mingo-modal-dialog__actions mingo-login-modal__actions">
          <button
            type="button"
            className="mingo-modal-dialog__btn mingo-modal-dialog__btn--secondary"
            onClick={closeLoginRequired}
          >
            {t('guest.cancel')}
          </button>
          <button type="submit" className="mingo-modal-dialog__btn mingo-modal-dialog__btn--primary" disabled={busy}>
            {busy ? t('write.saving') : t('guest.login')}
          </button>
        </div>
        <p className="mingo-login-modal__signup">
          <button type="button" className="mingo-login-modal__link" onClick={goSignup}>
            {t('auth.dontHaveAccount')} {t('auth.signupSubmit')}
          </button>
        </p>
      </form>
    </div>
  );
}

export default LoginModal;
